"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDownIcon, ArrowRightIcon, MoonIcon, ProfileIcon, SunIcon } from "@components/icons/header-icon";
import { ConfirmationDialog } from "@components/ui/ConfirmationDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@components/ui/DropdownMenu";
import { ColorModeContext, type ThemePreference } from "@contexts/color-mode";
import { useAuthStore } from "@store/useAuthStore";
import { cn } from "@utils/cn";
import Cookies from "js-cookie";
import { LogOut, Monitor } from "lucide-react";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { signOut } from "next-auth/react";
import { useContext, useEffect, useState } from "react";
import { useUser } from "./Hooks/use-profile";
const themeOptions: {
  key: ThemePreference;
  label: string;
}[] = [{
  key: "system",
  label: "System"
}, {
  key: "light",
  label: "Light"
}, {
  key: "dark",
  label: "Dark"
}];
export const AccountMenu = () => {
  const $ = _c(74);
  const router = useRouter();
  const params = useParams();
  const branchName = params.branch_id;
  const {
    mode,
    preference,
    setPreference
  } = useContext(ColorModeContext);
  const {
    user
  } = useUser();
  const [version, setVersion] = useState("");
  const [logoutOpen, setLogoutOpen] = useState(false);
  const profileImageSrc = user?.profileImage?.url || user?.media?.url || "/images/Default_pfp.svg";
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      fetch("/jsons/version.json").then(_temp).then(data => {
        setVersion(data.version);
      });
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== router) {
    t2 = async () => {
      ;
      try {
        await signOut({
          redirect: false
        });
        Cookies.remove("access_token");
        Cookies.remove("user_id");
        Cookies.remove("id");
        Cookies.remove("branch_id");
        Cookies.remove("user_branches");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("id");
        localStorage.removeItem("branch_id");
        localStorage.removeItem("user_branches");
        useAuthStore.getState().logout();
        router.push("/login");
      } catch (t3) {
        const error = t3;
        console.error(error);
      }
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLogout = t2;
  let t3;
  if ($[4] !== mode || $[5] !== preference) {
    t3 = preference === "system" ? <Monitor className="size-4" /> : mode === "dark" ? <MoonIcon /> : <SunIcon />;
    $[4] = mode;
    $[5] = preference;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const appearanceIcon = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex h-[42px] w-[72px] items-center gap-2 rounded-lg border border-border bg-background px-2", "outline-none transition-all duration-200 ease-out", "hover:border-primary/60 hover:bg-accent hover:shadow-sm", "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1", "data-[state=open]:border-primary data-[state=open]:bg-accent data-[state=open]:shadow-md", "data-[state=open]:[&>span]:rotate-180", "active:scale-[0.98]");
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== profileImageSrc) {
    t5 = <Image alt="profile" src={profileImageSrc} width={28} height={28} className="aspect-square h-7 w-7 rounded-lg object-cover ring-1 ring-border transition-transform duration-200 hover:scale-105" />;
    $[8] = profileImageSrc;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="inline-flex transition-transform duration-200"><ArrowDownIcon /></span>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = <DropdownMenuTrigger asChild={true}><button type="button" className={t4} aria-label="Account menu" aria-haspopup="menu">{t5}{t6}</button></DropdownMenuTrigger>;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== profileImageSrc) {
    t8 = <Image alt="profile" src={profileImageSrc} width={44} height={44} className="aspect-square rounded-[7.36px] object-cover" />;
    $[13] = profileImageSrc;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = user?.name;
  let t10;
  if ($[15] !== t9) {
    t10 = <p className="mb-1 text-sm font-bold">{t9}</p>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = user?.email;
  let t12;
  if ($[17] !== t11) {
    t12 = <p className="text-xs font-medium text-muted-foreground">{t11}</p>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12) {
    t13 = <div className="font-mont">{t10}{t12}</div>;
    $[19] = t10;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t8) {
    t14 = <DropdownMenuLabel className="font-normal"><div className="flex items-center gap-3 py-2">{t8}{t13}</div></DropdownMenuLabel>;
    $[22] = t13;
    $[23] = t8;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuSeparator />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== branchName || $[27] !== router) {
    t16 = () => router.push(`/${branchName}/profile`);
    $[26] = branchName;
    $[27] = router;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = `flex items-center gap-2 ${mode === "dark" ? "white-icon" : ""}`;
  let t18;
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ProfileIcon />;
    t19 = <p className="font-mont text-xs font-medium">Profile</p>;
    $[29] = t18;
    $[30] = t19;
  } else {
    t18 = $[29];
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t17) {
    t20 = <div className={t17}>{t18}{t19}</div>;
    $[31] = t17;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ArrowRightIcon />;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t20) {
    t22 = <div className="flex w-full items-center justify-between">{t20}{t21}</div>;
    $[34] = t20;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t16 || $[37] !== t22) {
    t23 = <DropdownMenuItem className="cursor-pointer py-2" onClick={t16}>{t22}</DropdownMenuItem>;
    $[36] = t16;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <DropdownMenuSeparator />;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const t25 = `cursor-pointer py-2 ${mode === "dark" ? "white-icon" : ""}`;
  let t26;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <p className="font-mont text-xs font-medium">Appearance</p>;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== appearanceIcon || $[42] !== t25) {
    t27 = <DropdownMenuSubTrigger className={t25}>{appearanceIcon}{t26}</DropdownMenuSubTrigger>;
    $[41] = appearanceIcon;
    $[42] = t25;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== setPreference) {
    t28 = value => setPreference(value as ThemePreference);
    $[44] = setPreference;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== preference) {
    t29 = themeOptions.map(option => <DropdownMenuRadioItem key={option.key} value={option.key} className="cursor-pointer"><span className={preference === option.key ? "font-semibold text-primary" : ""}>{option.label}</span></DropdownMenuRadioItem>);
    $[46] = preference;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== preference || $[49] !== t28 || $[50] !== t29) {
    t30 = <DropdownMenuSubContent><DropdownMenuRadioGroup value={preference} onValueChange={t28}>{t29}</DropdownMenuRadioGroup></DropdownMenuSubContent>;
    $[48] = preference;
    $[49] = t28;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t27 || $[53] !== t30) {
    t31 = <DropdownMenuSub>{t27}{t30}</DropdownMenuSub>;
    $[52] = t27;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <DropdownMenuSeparator />;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  let t34;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <DropdownMenuItem className="cursor-pointer bg-red-500 py-3 text-white focus:bg-red-500 focus:text-white" onClick={() => setLogoutOpen(true)}><LogOut className="size-4" /><p className="font-mont text-xs font-medium">Logout</p></DropdownMenuItem>;
    t34 = <DropdownMenuSeparator />;
    $[56] = t33;
    $[57] = t34;
  } else {
    t33 = $[56];
    t34 = $[57];
  }
  let t35;
  if ($[58] !== version) {
    t35 = <DropdownMenuLabel className="py-2 text-center font-normal"><p className="font-mont text-xs font-medium text-muted-foreground">version {version}</p></DropdownMenuLabel>;
    $[58] = version;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== t14 || $[61] !== t23 || $[62] !== t31 || $[63] !== t35) {
    t36 = <DropdownMenuContent align="end" className="min-w-[240px]">{t14}{t15}{t23}{t24}{t31}{t32}{t33}{t34}{t35}</DropdownMenuContent>;
    $[60] = t14;
    $[61] = t23;
    $[62] = t31;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== t36 || $[66] !== t7) {
    t37 = <DropdownMenu>{t7}{t36}</DropdownMenu>;
    $[65] = t36;
    $[66] = t7;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== handleLogout || $[69] !== logoutOpen) {
    t38 = <ConfirmationDialog open={logoutOpen} onOpenChange={setLogoutOpen} title="Logout" description="Are you sure you want to log out?" cancelText="Cancel" okText="Logout" variant="danger" onConfirm={handleLogout} />;
    $[68] = handleLogout;
    $[69] = logoutOpen;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t37 || $[72] !== t38) {
    t39 = <>{t37}{t38}</>;
    $[71] = t37;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  return t39;
};
function _temp(res) {
  return res.json();
}